import 'maplibre-gl/dist/maplibre-gl.css';
import './style.css';
import { createMapSnapshot, type GeographicMarker } from './map';
import { androidExternalBrowserUrl, detectKakaoBrowser } from './kakao-browser';
import { prepareVideo, storyVideoTitle } from './renderer';
import {
  analyzeTimelineJson,
  availableTimelineMonths,
  filterTimelineAnalysis,
  type TimelineAnalysis,
  type TimelineMonth,
} from './timeline';
import {
  createMp4,
  resolveVideoPreset,
  VIDEO_DURATIONS,
  type OutputFormat,
  type VideoDuration,
} from './video';
import { saveVideoFile } from './video-save';
import { relatedToolsMarkup, siteBrandMarkup } from './site-chrome';

function mountTimelineTool(): void {
type Stage = 'empty' | 'ready' | 'working' | 'done' | 'error';

const appElement = document.querySelector<HTMLDivElement>('#app');
if (!appElement) throw new Error('앱 컨테이너를 찾지 못했습니다.');
const app: HTMLDivElement = appElement;

let stage: Stage = 'empty';
let selectedFile: File | null = null;
let fullAnalysis: TimelineAnalysis | null = null;
let analysis: TimelineAnalysis | null = null;
let availableMonths: TimelineMonth[] = [];
let startMonth = '';
let endMonth = '';
let addCountries: ((value: TimelineAnalysis) => TimelineAnalysis) | null = null;
let outputFormat: OutputFormat = 'story';
let outputDuration: VideoDuration = 30;
let outputTitle = '';
let progressValue = 0;
let progressText = '';
let resultUrl: string | null = null;
let resultFile: File | null = null;
let errorMessage = '';
let guideOpen = false;
let guidePlatform: 'android' | 'ios' = 'android';
let generationController: AbortController | null = null;

const toolPath = '/tools/google-timeline-video/';
const kakaoPreview = import.meta.env.DEV
  ? new URLSearchParams(location.search).get('kakao-preview')
  : null;
const browserUserAgent = kakaoPreview === 'android'
  ? 'Mozilla/5.0 (Linux; Android 14) AppleWebKit/537.36 Mobile KAKAOTALK/10.8.3 (INAPP)'
  : kakaoPreview === 'ios'
    ? 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_5_1) AppleWebKit/605.1.15 Mobile KAKAOTALK/10.8.2 (INAPP)'
    : navigator.userAgent;
const kakaoBrowser = detectKakaoBrowser(browserUserAgent);
const toolUrl = new URL(toolPath, location.origin).href;
const externalBrowserUrl = kakaoBrowser.isAndroid
  ? androidExternalBrowserUrl(kakaoPreview ? `https://oneultool.com${toolPath}` : toolUrl)
  : null;

function icon(name: 'upload' | 'file' | 'check' | 'close' | 'download' | 'share' | 'info'): string {
  const paths = {
    upload: '<path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 15v4h14v-4"/>',
    file: '<path d="M7 3h7l4 4v14H7zM14 3v5h5"/>',
    check: '<path d="m5 12 4 4L19 6"/>',
    close: '<path d="m6 6 12 12M18 6 6 18"/>',
    download: '<path d="M12 4v11m0 0-4-4m4 4 4-4M5 19h14"/>',
    share: '<path d="M12 16V4m0 0L8 8m4-4 4 4M5 12v8h14v-8"/>',
    info: '<circle cx="12" cy="12" r="9"/><path d="M12 11v6M12 7.5v.5"/>',
  } as const;
  return `<svg aria-hidden="true" viewBox="0 0 24 24">${paths[name]}</svg>`;
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
  })[character]!);
}

function canShareResultFile(): boolean {
  if (!resultFile || !navigator.share || !navigator.canShare) return false;
  try {
    return navigator.canShare({ files: [resultFile] });
  } catch {
    return false;
  }
}

function kakaoBrowserNoticeMarkup(mode: 'before-file' | 'after-result'): string {
  if (!kakaoBrowser.isKakao) return '';
  const recommendedBrowser = kakaoBrowser.isIOS ? 'Safari' : 'Chrome·Samsung Internet';
  const copyButtonClass = externalBrowserUrl ? 'button button-soft' : 'button button-kakao-browser';
  const description = mode === 'before-file'
    ? `파일을 고르기 전에 ${recommendedBrowser}에서 열면 영상 저장이 가장 안정적이에요.`
    : '완성 영상은 서버에 올리지 않아 다른 브라우저로 자동 전달할 수 없어요. 저장이 반응하지 않으면 주소를 옮긴 뒤 JSON 파일만 다시 선택해 주세요.';
  return `
    <aside class="kakao-browser-notice ${mode}" role="note" aria-label="카카오톡 브라우저 저장 안내">
      <span class="kakao-browser-badge">카카오톡 브라우저</span>
      <strong>${mode === 'before-file' ? '영상 저장 전에 브라우저를 바꾸는 게 가장 편해요' : '저장 창이 반응하지 않을 때'}</strong>
      <p>${description}</p>
      <div class="kakao-browser-actions">
        ${externalBrowserUrl ? `<a class="button button-kakao-browser" href="${escapeHtml(externalBrowserUrl)}">기본 브라우저로 열기</a>` : ''}
        <button id="copy-browser-url" class="${copyButtonClass}" type="button">주소 복사</button>
      </div>
      <small>또는 카카오톡의 더보기(⋮)에서 ‘다른 브라우저로 열기’를 선택하세요.</small>
    </aside>`;
}

function legalPage(): 'privacy' | 'terms' | null {
  if (location.pathname.startsWith('/privacy')) return 'privacy';
  if (location.pathname.startsWith('/terms')) return 'terms';
  return null;
}

function renderLegal(page: 'privacy' | 'terms'): void {
  const content = page === 'privacy' ? `
    <p class="legal-date">시행일 2026년 9월 6일</p>
    <h2>1. 한눈에 보는 처리 방식</h2>
    <p>내 역마살 추적기는 선택한 Timeline JSON 원본을 서버로 업로드하지 않습니다. 파일 읽기, 좌표 정리, 지도 합성, MP4 생성은 사용자의 브라우저 안에서 이루어집니다. 파도콩 배구의 CPU 대전, 퇴근은 함정과 사장님 몰래 퇴근하기의 게임 진행, 오늘의 한입 운세 계산과 내 대답은 링크 생성도 브라우저 안에서 실행됩니다. 친구 방을 사용하는 온라인 게임의 승패와 진행 상태는 별도 경기 서버가 확정합니다.</p>
    <h2>2. 외부 지도 요청</h2>
    <p>영상의 배경 지도를 만들 때 OpenFreeMap 서버에서 지도 스타일과 타일을 내려받습니다. 이 과정에서 일반적인 웹 요청 정보(IP 주소, 브라우저 정보)와 요청한 지도 타일 영역이 OpenFreeMap에 전달될 수 있지만, Timeline JSON 원본은 전달하지 않습니다.</p>
    <h2>3. 저장과 전송</h2>
    <p>오늘툴 서버는 선택한 타임라인 파일과 완성 영상을 저장하지 않습니다. 완성 영상은 브라우저 메모리에 잠시 머물며 사용자가 저장하거나 페이지를 닫으면 정리됩니다. 오늘의 한입 운세에서 ‘이 기기에 생일 기억하기’를 선택하면 생년월일이, 사장님 몰래 퇴근하기를 플레이하면 레벨별 최고 기록이 해당 브라우저의 로컬 저장소에 보관됩니다.</p>
    <p>나를 카드로 뽑아!에서 선택한 사진과 입력한 이름·특수기는 브라우저 메모리에서만 처리하며 오늘툴 서버나 브라우저의 영구 저장소에 보관하지 않습니다. 사진과 완성 카드는 이용 분석 이벤트로 전송하지 않습니다. 이미지 저장은 기기로 내려받는 동작이며, 기기 공유를 직접 선택하면 카드 이미지가 이용자가 선택한 앱에 전달됩니다. 카드 등급과 능력치는 사진 분석 없이 무작위로 생성됩니다.</p>
    <h2>4. 온라인 친구 방 게임</h2>
    <p>파도콩 배구, 파도콩 그림릴레이, 파도콩 꼬리잡기와 콩포트리스의 온라인 친구 방을 이용하면 닉네임, 방 코드, 게임 조작 입력, 경기 상태가 접속에 필요한 일반적인 네트워크 정보(IP 주소 등)와 함께 경기 서버로 전송됩니다. 그림릴레이에서는 제시어, 그림 획과 답도 게임 진행을 위해 전송됩니다. 방과 게임 내용은 서버 메모리에 일시적으로만 유지되며 계정, 채팅, 영구 전적 데이터베이스는 사용하지 않습니다. 재접속용 비밀 토큰은 현재 브라우저 탭의 세션 저장소에 보관됩니다.</p>
    <h2>5. 운세 카드 공유</h2>
    <p>운세 결과 이미지는 생년월일을 포함하지 않고 브라우저에서 생성됩니다. 카카오톡 카드 공유가 활성화된 환경에서는 사용자가 공유 버튼을 누른 경우에만 결과 이미지가 카카오 서버로 전송되며, 카카오 정책에 따라 최대 100일간 보관된 뒤 자동 삭제됩니다. 그 밖의 환경에서는 기기의 공유 기능을 사용하거나 이미지 파일로 저장합니다.</p>
    <h2>6. 내 대답은 링크 공유</h2>
    <p>내 대답은에서 입력한 문구와 선택한 연출은 서버 데이터베이스에 저장하지 않고 공유 주소의 조각 식별자에 인코딩합니다. 인코딩은 암호화가 아니므로 링크를 받은 사람은 내용을 확인할 수 있습니다. 카카오톡 또는 기기 공유 기능을 선택하면 인코딩된 문구가 포함된 공유 주소가 해당 공유 서비스에 전달됩니다. 카카오톡 공유 카드는 답이 없는 고정 이미지를 사용하며, 입력한 답을 별도 이미지로 만들어 카카오 이미지 서버에 업로드하지 않습니다.</p>
    <h2>7. 이용 분석 및 쿠키</h2>
    <p>오늘툴은 방문 현황과 서비스 개선에 필요한 통계를 확인하기 위해 Google 애널리틱스 4를 사용합니다. 이 과정에서 방문·세션 수, 페이지 주소와 제목, 이전 페이지, 브라우저·기기 정보, 대략적인 지역, 페이지 조회, 스크롤, 외부 링크 클릭, 참여 시간이 수집될 수 있으며 이용자와 세션을 구분하기 위한 자사 쿠키(_ga 등)가 저장될 수 있습니다.</p>
    <p>페이지 주소에서는 캠페인 식별용 utm 매개변수를 제외한 검색 매개변수와 조각 식별자를 제거해 전송합니다. Timeline JSON 원본·파일명·완성 영상, 운세에 입력한 생년월일, 내 대답은에 입력한 문구와 전체 공유 링크, 온라인 친구 방의 닉네임·방 코드·조작·제시어·그림·답은 Google 애널리틱스 맞춤 이벤트로 전송하지 않습니다. 광고 개인 최적화 신호와 Google Signals도 사용하지 않습니다.</p>
    <p>Google은 IP 주소를 서비스 제공과 대략적인 지역 산출에 사용한 뒤 원시 IP 주소를 폐기한다고 안내합니다. 자세한 처리 방식은 <a href="https://policies.google.com/technologies/partner-sites?hl=ko" target="_blank" rel="noreferrer">Google 서비스 사용 사이트의 정보 처리 안내</a>에서 확인할 수 있습니다. 이용자는 브라우저에서 쿠키를 차단·삭제하거나 <a href="https://tools.google.com/dlpage/gaoptout?hl=ko" target="_blank" rel="noreferrer">Google 애널리틱스 차단 브라우저 부가기능</a>을 사용할 수 있습니다.</p>
    <h2>8. 폰트</h2>
    <p>Lexend Deca와 Noto Sans KR 파일은 오늘툴 서버에서 직접 제공하므로 글꼴을 받기 위한 Google Fonts 요청은 발생하지 않습니다.</p>
    <h2>9. 이용자의 선택</h2>
    <p>언제든 페이지를 닫아 처리를 중단할 수 있습니다. 민감한 위치 기록인 만큼 개인 기기에서 이용하고 완성 파일의 공유 범위를 직접 확인해 주세요.</p>
  ` : `
    <p class="legal-date">시행일 2026년 8월 27일</p>
    <h2>1. 서비스 성격</h2>
    <p>오늘툴은 타임라인 영상 변환 같은 작은 도구, 오늘의 한입 운세와 내 대답은 같은 재미 콘텐츠, 파도콩 친구 방 게임을 제공하는 무료 서비스입니다.</p>
    <h2>2. 정확성</h2>
    <p>영상은 JSON 안에서 읽을 수 있는 좌표를 시간순으로 자연스럽게 연결한 표현입니다. 실제 이동 도로, 교통수단, 방문 여부를 증명하거나 정확하게 복원하지 않습니다. 오늘의 한입 운세는 오락과 가벼운 자기 성찰을 위한 콘텐츠이며, 의료·재정·법률 등 전문적인 판단이나 미래의 사실을 제공하지 않습니다.</p>
    <h2>3. 이용 책임</h2>
    <p>위치 기록과 완성 영상에는 민감한 개인 정보가 포함될 수 있습니다. 내 대답은 링크에 비밀번호·연락처 같은 개인정보나 타인을 모욕·위협하는 문구를 입력하지 마세요. 파일과 링크의 보관 및 공개·공유에 대한 책임은 이용자에게 있습니다.</p>
    <h2>4. 가용성</h2>
    <p>브라우저의 영상 인코딩 지원, 기기 성능, 지도 제공 상태에 따라 생성이 실패할 수 있습니다. 온라인 게임은 통신 상태나 경기 서버 점검으로 중단될 수 있습니다. 오늘툴은 중요한 원본의 유일한 보관 장소로 사용하면 안 됩니다.</p>
    <h2>5. 제3자 자료</h2>
    <p>배경 지도는 OpenFreeMap, OpenMapTiles, OpenStreetMap 자료를 사용하며 완성 영상에 출처가 표시됩니다. Google 지도와 Google Timeline은 Google의 서비스이며 오늘툴은 Google과 제휴한 서비스가 아닙니다.</p>
  `;
  const title = page === 'privacy' ? '개인정보처리방침' : '이용약관';
  document.title = `${title} | 오늘툴`;
  app.innerHTML = `
    <div class="page-shell legal-shell">
      <header class="site-brand-header">${siteBrandMarkup()}</header>
      <main class="legal-card">
        <a class="back-link" href="${toolPath}">← 내 역마살 추적기로 돌아가기</a>
        <h1>${title}</h1>
        ${content}
      </main>
      ${footerMarkup()}
    </div>`;
}

function formatMarkup(): string {
  return `
    <fieldset class="format-picker">
      <legend>영상 형식</legend>
      <label class="format-option ${outputFormat === 'story' ? 'selected' : ''}">
        <input type="radio" name="format" value="story" ${outputFormat === 'story' ? 'checked' : ''} />
        <span class="format-shape story-shape" aria-hidden="true"></span>
        <span><strong>인스타 스토리용</strong><small>9:16 세로</small></span>
        <span class="format-check">${icon('check')}</span>
      </label>
      <label class="format-option ${outputFormat === 'post' ? 'selected' : ''}">
        <input type="radio" name="format" value="post" ${outputFormat === 'post' ? 'checked' : ''} />
        <span class="format-shape post-shape" aria-hidden="true"></span>
        <span><strong>인스타 게시물용</strong><small>1:1 정사각</small></span>
        <span class="format-check">${icon('check')}</span>
      </label>
    </fieldset>`;
}

function durationMarkup(): string {
  return `
    <fieldset class="duration-picker">
      <legend>영상 길이 <small>길수록 이동을 더 천천히 보여줘요</small></legend>
      <div class="duration-options">
        ${VIDEO_DURATIONS.map((duration) => `
          <label class="duration-option ${outputDuration === duration ? 'selected' : ''}">
            <input type="radio" name="duration" value="${duration}" ${outputDuration === duration ? 'checked' : ''} />
            <span>${duration}초</span>
          </label>`).join('')}
      </div>
    </fieldset>`;
}

function titleMarkup(): string {
  const defaultTitle = storyVideoTitle(outputDuration);
  return `
    <label class="video-title-field" for="video-title">
      <span>영상 제목 <small>선택</small></span>
      <input
        id="video-title"
        type="text"
        value="${escapeHtml(outputTitle)}"
        placeholder="${defaultTitle}"
        maxlength="28"
        aria-describedby="video-title-hint"
      />
      <small id="video-title-hint">비워두면 ‘${defaultTitle}’로 표시돼요.</small>
    </label>`;
}

function monthOptions(selected: string): string {
  return availableMonths
    .map((month) => `<option value="${month.value}" ${month.value === selected ? 'selected' : ''}>${month.label}</option>`)
    .join('');
}

function periodMarkup(): string {
  return `
    <fieldset class="period-picker">
      <legend>영상 기간</legend>
      <div class="period-fields">
        <label><span>시작</span><select id="start-month" aria-label="영상 시작 월">${monthOptions(startMonth)}</select></label>
        <span class="period-separator" aria-hidden="true">–</span>
        <label><span>종료</span><select id="end-month" aria-label="영상 종료 월">${monthOptions(endMonth)}</select></label>
      </div>
    </fieldset>`;
}

function cardMarkup(): string {
  if (stage === 'empty') {
    return `
      <section class="tool-card empty-card" aria-labelledby="upload-title">
        ${kakaoBrowserNoticeMarkup('before-file')}
        <div id="dropzone" class="dropzone">
          <div class="upload-icon">${icon('upload')}</div>
          <h2 id="upload-title">나의 타임라인을 불러와요</h2>
          <p>Google 지도에서 내보낸 JSON 파일을 선택해 주세요.</p>
          <button id="choose-file" class="button button-primary" type="button">Timeline JSON 파일 추가</button>
          <input id="timeline-file" type="file" accept=".json,application/json" hidden />
          <span class="drop-hint">PC에서는 파일을 이곳에 놓아도 돼요</span>
        </div>
        <div class="timeline-requirement" role="note" aria-label="타임라인 이용 전 확인">
          <span class="timeline-requirement-icon" aria-hidden="true">!</span>
          <span class="timeline-requirement-copy">
            <strong>Google 지도 타임라인 기록이 있어야 사용할 수 있어요</strong>
            <small>타임라인을 켠 적이 없거나 이동 데이터가 없다면 내보낼 파일이 없어 영상을 만들 수 없습니다.</small>
          </span>
        </div>
        <button id="open-guide" class="button button-soft" type="button">${icon('info')}타임라인 내보내는 법</button>
        <div class="privacy-note"><p>원본 파일은 업로드하지 않고 브라우저에서 처리해요.</p><p>영상 지도를 만들기 위해 주변 지도 타일을 불러와요.</p></div>
      </section>`;
  }
  if (stage === 'ready') {
    return `
      <section class="tool-card ready-card" aria-labelledby="ready-title">
        <div class="selected-file">
          <span class="file-icon">${icon('file')}</span>
          <span class="file-copy"><strong id="ready-title">${escapeHtml(selectedFile?.name ?? 'Timeline.json')}</strong><small>파일을 안전하게 읽었어요</small></span>
          <button id="change-file" class="text-button" type="button">변경</button>
        </div>
        ${periodMarkup()}
        ${titleMarkup()}
        ${formatMarkup()}
        ${durationMarkup()}
        <button id="create-video" class="button button-primary" type="button">${outputDuration}초 영상 만들기</button>
        <button id="open-guide" class="guide-link" type="button">타임라인 내보내는 법</button>
      </section>`;
  }
  if (stage === 'working') {
    return `
      <section class="tool-card working-card" aria-labelledby="working-title" aria-live="polite">
        <div class="working-mark"><span></span><span></span><span></span></div>
        <h2 id="working-title">영상을 만들고 있어요</h2>
        <p class="working-note">이 탭은 닫지 말고 열어두세요.</p>
        <div class="progress-row"><span id="progress-text">${escapeHtml(progressText)}</span><strong id="progress-percent">${Math.round(progressValue)}%</strong></div>
        <progress id="generation-progress" max="100" value="${progressValue}">${progressValue}%</progress>
        <button id="cancel-video" class="button button-soft" type="button">취소</button>
        <p class="local-reminder">파일과 영상은 서버에 저장되지 않아요.</p>
      </section>`;
  }
  if (stage === 'done') {
    const shareSupported = canShareResultFile();
    const saveLabel = kakaoBrowser.isKakao && shareSupported
      ? `${icon('share')}공유해서 저장하기`
      : `${icon('download')}${kakaoBrowser.isKakao ? '영상 저장 시도하기' : '영상 저장하기'}`;
    return `
      <section class="tool-card done-card" aria-labelledby="done-title">
        <div class="done-check">${icon('check')}</div>
        <h2 id="done-title">내 역마살 영상이 완성됐어요!</h2>
        <p>${outputDuration}초 영상을 재생해서 확인한 뒤 저장해 주세요.</p>
        <video class="result-video ${outputFormat}" src="${resultUrl ?? ''}" controls playsinline preload="metadata"></video>
        <button id="save-video" class="button button-primary" type="button">${saveLabel}</button>
        ${kakaoBrowser.isKakao ? '' : `<button id="share-video" class="button button-soft share-button" type="button">${icon('share')}바로 공유하기</button>`}
        ${kakaoBrowserNoticeMarkup('after-result')}
        <button id="change-period" class="button button-soft" type="button">기간 다시 정하기</button>
        <button id="reset-file" class="guide-link" type="button">다른 파일로 만들기</button>
      </section>`;
  }
  return `
    <section class="tool-card error-card" role="alert" aria-labelledby="error-title">
      <div class="error-mark">!</div>
      <h2 id="error-title">잠깐, 영상을 만들지 못했어요.</h2>
      <p>${escapeHtml(errorMessage)}</p>
      <button id="retry" class="button button-primary" type="button">다른 파일로 다시 시도</button>
      <button id="open-guide" class="guide-link" type="button">타임라인 내보내는 법</button>
    </section>`;
}

function guideMarkup(): string {
  const android = [
    ['휴대폰 설정 열기', 'Google 지도 앱이 아니라 휴대폰의 설정 앱을 열어요.'],
    ['위치 메뉴로 이동', '위치 → 위치 서비스 → 타임라인을 차례로 눌러요.'],
    ['타임라인 데이터 내보내기', '내보내기 버튼을 누르고 계속을 선택해요.'],
    ['JSON 파일 저장', '원하는 폴더를 고른 뒤 저장하고 오늘툴에서 그 파일을 선택해요.'],
  ];
  const ios = [
    ['Google 지도 설정 열기', 'Google 지도 앱에서 프로필 사진 → 설정을 눌러요.'],
    ['개인 콘텐츠 선택', '계정 설정 아래의 개인 콘텐츠로 들어가요.'],
    ['타임라인 데이터 내보내기', '위치 설정에서 타임라인 데이터 내보내기를 눌러요.'],
    ['파일 앱에 저장', '공유 화면에서 파일에 저장을 누른 뒤 location-history.json을 선택해요.'],
  ];
  const steps = guidePlatform === 'android' ? android : ios;
  const official = guidePlatform === 'android'
    ? 'https://support.google.com/maps/answer/14169818?co=GENIE.Platform%3DAndroid&hl=ko'
    : 'https://support.google.com/maps/answer/6258979?co=GENIE.Platform%3DiOS&hl=ko';
  return `
    <dialog id="guide-dialog" class="guide-dialog" aria-labelledby="guide-title">
      <div class="guide-header"><h2 id="guide-title">타임라인 내보내는 법</h2><button id="close-guide" class="icon-button" type="button" aria-label="안내 닫기">${icon('close')}</button></div>
      <div class="guide-tabs" role="tablist" aria-label="휴대폰 종류">
        <button type="button" role="tab" data-platform="android" aria-selected="${guidePlatform === 'android'}">Android</button>
        <button type="button" role="tab" data-platform="ios" aria-selected="${guidePlatform === 'ios'}">iPhone · iPad</button>
      </div>
      <ol class="guide-steps">
        ${steps.map(([title, description], index) => `<li><span>${index + 1}</span><div><strong>${title}</strong><p>${description}</p></div></li>`).join('')}
      </ol>
      <p class="guide-filename">내보낸 파일 이름은 기기에 따라 <strong>Timeline.json</strong> 또는 <strong>location-history.json</strong>일 수 있어요.</p>
      <a class="official-link" href="${official}" target="_blank" rel="noreferrer">Google 공식 안내 보기 ↗</a>
    </dialog>`;
}

function footerMarkup(): string {
  return `
    <footer class="legal-footer">
      <a href="/privacy/">개인정보처리방침</a>
      <a href="/terms/">이용약관</a>
      <span>© 2026 오늘툴</span>
    </footer>`;
}

function otherToolsMarkup(): string {
  const heading = stage === 'working'
    ? '영상 만드는 동안, 다른 오늘툴 한 판?'
    : '다른 오늘툴도 둘러보세요';
  return relatedToolsMarkup('google-timeline-video', {
    heading,
    openInNewTab: stage === 'working',
  });
}

function render(): void {
  const legal = legalPage();
  if (legal) {
    renderLegal(legal);
    return;
  }
  document.title = '내 역마살 추적기 | 오늘툴';
  app.innerHTML = `
    <div class="page-shell">
      <div class="blob blob-one" aria-hidden="true"></div><div class="blob blob-two" aria-hidden="true"></div><div class="blob blob-three" aria-hidden="true"></div>
      <header class="site-brand-header">${siteBrandMarkup()}</header>
      <main>
        <section class="tool-intro">
          <span class="tool-kicker">오늘의 장난감</span>
          <h1>내 역마살 추적기</h1>
          <p>어디까지 돌아다녔나, 내 타임라인 영상으로 확인해요.</p>
        </section>
        ${cardMarkup()}
        ${otherToolsMarkup()}
      </main>
      ${footerMarkup()}
      ${guideMarkup()}
      <div id="toast" class="toast" role="status" aria-live="polite"></div>
    </div>`;
  bindEvents();
  if (guideOpen) {
    requestAnimationFrame(() => document.querySelector<HTMLDialogElement>('#guide-dialog')?.showModal());
  }
}

function bindEvents(): void {
  const fileInput = document.querySelector<HTMLInputElement>('#timeline-file');
  document.querySelector('#choose-file')?.addEventListener('click', () => fileInput?.click());
  document.querySelector('#change-file')?.addEventListener('click', () => {
    stage = 'empty';
    render();
    requestAnimationFrame(() => document.querySelector<HTMLInputElement>('#timeline-file')?.click());
  });
  fileInput?.addEventListener('change', () => {
    const file = fileInput.files?.[0];
    if (file) void loadFile(file);
  });

  const dropzone = document.querySelector<HTMLElement>('#dropzone');
  dropzone?.addEventListener('dragover', (event) => { event.preventDefault(); dropzone.classList.add('dragging'); });
  dropzone?.addEventListener('dragleave', () => dropzone.classList.remove('dragging'));
  dropzone?.addEventListener('drop', (event) => {
    event.preventDefault();
    dropzone.classList.remove('dragging');
    const file = event.dataTransfer?.files[0];
    if (file) void loadFile(file);
  });

  document.querySelectorAll<HTMLInputElement>('input[name="format"]').forEach((input) => {
    input.addEventListener('change', () => { outputFormat = input.value as OutputFormat; render(); });
  });
  document.querySelectorAll<HTMLInputElement>('input[name="duration"]').forEach((input) => {
    input.addEventListener('change', () => {
      outputDuration = Number(input.value) as VideoDuration;
      render();
    });
  });
  const titleInput = document.querySelector<HTMLInputElement>('#video-title');
  titleInput?.addEventListener('input', () => { outputTitle = titleInput.value; });
  document.querySelector<HTMLSelectElement>('#start-month')?.addEventListener('change', (event) => {
    const nextStart = (event.currentTarget as HTMLSelectElement).value;
    startMonth = nextStart;
    if (startMonth > endMonth) endMonth = startMonth;
    applyMonthRange();
  });
  document.querySelector<HTMLSelectElement>('#end-month')?.addEventListener('change', (event) => {
    const nextEnd = (event.currentTarget as HTMLSelectElement).value;
    endMonth = nextEnd;
    if (endMonth < startMonth) startMonth = endMonth;
    applyMonthRange();
  });
  document.querySelector('#create-video')?.addEventListener('click', () => void generateVideo());
  document.querySelector('#cancel-video')?.addEventListener('click', () => generationController?.abort());
  document.querySelector('#retry')?.addEventListener('click', reset);
  document.querySelector('#reset-file')?.addEventListener('click', reset);
  document.querySelector('#change-period')?.addEventListener('click', returnToPeriodSelection);
  document.querySelector('#open-guide')?.addEventListener('click', () => { guideOpen = true; render(); });
  document.querySelector('#close-guide')?.addEventListener('click', closeGuide);
  const dialog = document.querySelector<HTMLDialogElement>('#guide-dialog');
  dialog?.addEventListener('cancel', (event) => { event.preventDefault(); closeGuide(); });
  dialog?.addEventListener('click', (event) => {
    if (event.target === dialog) closeGuide();
  });
  document.querySelectorAll<HTMLButtonElement>('[data-platform]').forEach((button) => {
    button.addEventListener('click', () => {
      guidePlatform = button.dataset.platform as 'android' | 'ios';
      guideOpen = true;
      render();
    });
  });
  document.querySelector('#share-video')?.addEventListener('click', () => void shareResult());
  document.querySelector('#save-video')?.addEventListener('click', () => void saveResult());
  document.querySelector('#copy-browser-url')?.addEventListener('click', () => void copyToolUrl());
}

function returnToPeriodSelection(): void {
  revokeResult();
  stage = 'ready';
  render();
  requestAnimationFrame(() => {
    const startSelect = document.querySelector<HTMLSelectElement>('#start-month');
    startSelect?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    startSelect?.focus({ preventScroll: true });
  });
}

function closeGuide(): void {
  guideOpen = false;
  document.querySelector<HTMLDialogElement>('#guide-dialog')?.close();
}

function applyMonthRange(): void {
  if (!fullAnalysis || !addCountries) return;
  try {
    analysis = addCountries(filterTimelineAnalysis(fullAnalysis, startMonth, endMonth));
    revokeResult();
    stage = 'ready';
    render();
  } catch (error) {
    showError(error instanceof Error ? error.message : '선택한 기간의 기록을 읽지 못했어요.');
  }
}

async function loadFile(file: File): Promise<void> {
  if (!file.name.toLowerCase().endsWith('.json') && file.type !== 'application/json') {
    showError('JSON 파일만 선택할 수 있어요.');
    return;
  }
  if (file.size > 200 * 1024 * 1024) {
    showError('파일이 200MB보다 커서 브라우저에서 안전하게 처리하기 어려워요.');
    return;
  }
  try {
    const text = await file.text();
    const parsedAnalysis = analyzeTimelineJson(text);
    const countryModule = await import('./countries');
    fullAnalysis = parsedAnalysis;
    addCountries = countryModule.addCountryInformation;
    availableMonths = availableTimelineMonths(parsedAnalysis);
    startMonth = availableMonths[0]?.value ?? '';
    endMonth = availableMonths.at(-1)?.value ?? '';
    analysis = addCountries(parsedAnalysis);
    selectedFile = file;
    stage = 'ready';
    render();
  } catch (error) {
    showError(error instanceof Error ? error.message : '파일을 읽지 못했어요.');
  }
}

function updateProgress(value: number, label: string): void {
  progressValue = Math.max(progressValue, Math.min(100, value));
  progressText = label;
  const progress = document.querySelector<HTMLProgressElement>('#generation-progress');
  const percent = document.querySelector<HTMLElement>('#progress-percent');
  const text = document.querySelector<HTMLElement>('#progress-text');
  if (progress) progress.value = progressValue;
  if (percent) percent.textContent = `${Math.round(progressValue)}%`;
  if (text) text.textContent = label;
}

function mapMarkers(value: TimelineAnalysis): GeographicMarker[] {
  const markers: GeographicMarker[] = [];
  const farthestTrip = [...value.trips].sort((a, b) => b.distanceKm - a.distanceKm)[0];
  if (farthestTrip) {
    const country = value.countries.find((candidate) => candidate.code === farthestTrip.countryCode);
    markers.push({
      coordinate: farthestTrip.coordinate,
      kind: 'trip',
      label: country ? `${country.flag} 장거리 이동` : '장거리 이동',
    });
  }
  return markers;
}

async function generateVideo(): Promise<void> {
  if (!selectedFile || !analysis || analysis.points.length < 2) return;
  generationController = new AbortController();
  stage = 'working';
  progressValue = 1;
  progressText = '브라우저 기능을 확인하는 중';
  render();
  try {
    await document.fonts.ready;
    const preset = await resolveVideoPreset(outputFormat);
    updateProgress(8, '지명과 이동 경로를 준비하는 중');
    const snapshot = await createMapSnapshot(
      analysis.points,
      preset.width,
      preset.height,
      mapMarkers(analysis),
      generationController.signal,
    );
    updateProgress(22, '이동 경로를 영상으로 그리는 중');
    const canvas = document.createElement('canvas');
    canvas.width = preset.width;
    canvas.height = preset.height;
    const prepared = prepareVideo(snapshot, analysis, outputDuration, outputTitle);
    const blob = await createMp4(
      canvas,
      prepared,
      preset,
      generationController.signal,
      (fraction) => updateProgress(22 + fraction * 77, `${outputDuration}초 영상을 만드는 중`),
    );
    revokeResult();
    resultUrl = URL.createObjectURL(blob);
    resultFile = new File(
      [blob],
      `oneultool-timeline-${startMonth}-${endMonth}-${outputFormat}-${outputDuration}s.mp4`,
      { type: 'video/mp4' },
    );
    progressValue = 100;
    stage = 'done';
    render();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      stage = 'ready';
      render();
    } else {
      showError(error instanceof Error ? error.message : '영상을 만들지 못했어요. 잠시 후 다시 시도해 주세요.');
    }
  } finally {
    generationController = null;
  }
}

async function saveResult(): Promise<void> {
  if (!resultFile) return;
  try {
    const outcome = await saveVideoFile(resultFile);
    if (outcome === 'saved') showToast('영상 파일을 저장했어요.');
    if (outcome === 'shared') showToast('영상 파일을 선택한 앱으로 전달했어요.');
    if (outcome === 'downloaded') {
      showToast(kakaoBrowser.isKakao
        ? '다운로드를 요청했어요. 반응이 없으면 아래 안내대로 다른 브라우저에서 열어 주세요.'
        : '다운로드를 시작했어요. 다운로드 폴더를 확인해 주세요.');
    }
  } catch {
    showToast('저장하지 못했어요. 바로 공유하기를 이용해 주세요.');
  }
}

async function copyToolUrl(): Promise<void> {
  let copied = false;
  try {
    await navigator.clipboard.writeText(toolUrl);
    copied = true;
  } catch {
    try {
      const input = document.createElement('textarea');
      input.value = toolUrl;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.append(input);
      input.select();
      copied = document.execCommand('copy');
      input.remove();
    } catch {
      copied = false;
    }
  }
  if (copied) {
    const browserName = kakaoBrowser.isIOS ? 'Safari' : 'Chrome이나 Samsung Internet';
    showToast(`주소를 복사했어요. ${browserName} 주소창에 붙여넣어 주세요.`);
  } else {
    showToast('주소를 복사하지 못했어요. 카카오톡 더보기에서 다른 브라우저로 열어 주세요.');
  }
}

async function shareResult(): Promise<void> {
  if (!resultFile) return;
  if (navigator.share && navigator.canShare?.({ files: [resultFile] })) {
    try {
      await navigator.share({ title: storyVideoTitle(outputDuration, outputTitle), files: [resultFile] });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) showToast('공유하지 못했어요. 영상 저장하기를 이용해 주세요.');
    }
  } else {
    showToast('이 브라우저에서는 바로 공유할 수 없어요. 영상을 먼저 저장해 주세요.');
  }
}

function showToast(message: string): void {
  const toast = document.querySelector<HTMLElement>('#toast');
  if (!toast) return;
  toast.textContent = message;
  toast.classList.add('show');
  window.setTimeout(() => toast.classList.remove('show'), 3_000);
}

function showError(message: string): void {
  errorMessage = message;
  stage = 'error';
  render();
}

function revokeResult(): void {
  if (resultUrl) URL.revokeObjectURL(resultUrl);
  resultUrl = null;
  resultFile = null;
}

function reset(): void {
  generationController?.abort();
  revokeResult();
  selectedFile = null;
  fullAnalysis = null;
  analysis = null;
  availableMonths = [];
  startMonth = '';
  endMonth = '';
  outputTitle = '';
  stage = 'empty';
  render();
}

window.addEventListener('beforeunload', revokeResult);
render();
}

if (location.pathname.startsWith('/games/escape-the-office')) {
  void import('./devil-game').then(({ mountDevilGame }) => mountDevilGame());
} else {
  mountTimelineTool();
}
